import { CardString } from 'pokersolver'

import { ActionHint } from '../../ActionEvaluator'
import { Player } from '../../Player'
import { TableSnapshot } from '../../Snapshot'
import { BotAction } from '../BotPlayerKind.utils'
import * as HandDetectionUtils from '../BotKindPP/HandDetectionUtils'
import { Game } from '../../Game'
import { analyzeDraws } from './AnalyzeDraws'
import { BoardStateTag } from '../../states'
import { logger } from '../../../util'

function getChance(chanceMatch = 1) {
  const chance = Math.random()
  return chance <= chanceMatch
}

type BotActionCondition = {
  type: string
  value?: unknown
}
export type BotActionResponse = BotAction & {
  condition: BotActionCondition[]
}

export type BotActionCallbackArgs = { game: Game; table: TableSnapshot; player: Player }
export type BotActionCallback = (args: BotActionCallbackArgs) => BotActionResponse

function getAggressorInStreet(street: BoardStateTag, { table, player }: Omit<BotActionCallbackArgs, 'game'>) {
  const aggressorEvent = table?.streetAggressorEvent?.find((e) => e.street === street)
  // Check if there is an aggressor event
  const isAggressor = aggressorEvent?.playerId === player.id
  return {
    isAggressor,
    aggressorEvent
  }
}

const CHANCE_CALL_FLOP = 0.7

type FlopOptions = {
  useChance?: number
}
const Flop = function ({ game, table, player }: BotActionCallbackArgs, options?: FlopOptions): BotActionResponse {
  // Default options
  const { useChance = CHANCE_CALL_FLOP } = options || {}

  const cards = getPlayingCards({ player, table })

  const { isAggressor: isPreFlopAggressor, aggressorEvent } = getAggressorInStreet('deal', { table, player })

  const handDraws = analyzeDraws({ table, player })
  const hasDraw = handDraws.draws.length > 0
  const hasPair = HandDetectionUtils.hasAPair()(cards)

  const betInBoard = !(table.biggestBet === 0)

  // More than 3 opponents in play
  const moreThan3Players =
    game.defaultTable.players.filter((p) => p.id !== player.id && p.isInPlay() && !p.isFolded() && !p.isSittingOut())
      .length >= 3

  /**
   * AGGRESSOR
   * 1.- Should continue to bet (C-Bet) 70% of the time, and the bet should be ½ pot.
   * 2.- If facing more than 3 opponents, they can check if they don’t have a strong holding or if the board has a lot of draws
   */
  if (isPreFlopAggressor) {
    // 2. More than 3 players
    const hasStrongHolding = handDraws.isStrong || hasDraw
    if (moreThan3Players && !hasStrongHolding && !betInBoard) {
      // No bets, check
      return {
        action: ActionHint.CHECK,
        amount: 0,
        condition: [{ type: 'MoreThan3Players' }, { type: 'NoStrongHand' }, { type: 'NoBetInBoard' }]
      }
    }
    if (moreThan3Players && !hasStrongHolding && betInBoard) {
      // Bet in board, fold
      return {
        action: ActionHint.FOLD,
        amount: 0,
        condition: [{ type: 'MoreThan3Players' }, { type: 'NoStrongHand' }]
      }
    }

    // 1. Should C-Bet
    const hasChanceToBet = getChance(useChance)
    if (!hasChanceToBet) {
      // Not feeling Lucky, it will try to CHECK OR FOLD
      return getSoftAction({ player }, { condition: [{ type: 'Unlucky' }] })
    }
    // Bet or Raise
    let action = ActionHint.BET
    let amount = Math.round(table.mainPot * 0.5)
    if (betInBoard) {
      // Raise
      action = ActionHint.RAISE
      amount = table.biggestBet * 3
    }
    const actionToDo = doBetActionOrAllIn({ action, amount, table, player })
    actionToDo.condition.push({ type: 'IsPreFlopAggressor' })
    return actionToDo
  }

  /**
   * NON AGGRESSOR
   * 1.- A player should only call the C-Bet if they have a pair or a draw
   * 2.- Soft action, try to Check or Fold
   */
  if (betInBoard && (hasPair || hasDraw)) {
    // 1.
    const action = ActionHint.CALL
    const amount = getAmountForAction({ action, table, player })
    const actionToDo = doBetActionOrAllIn({ action, amount, table, player })

    hasPair && actionToDo.condition.push({ type: 'Pair', value: JSON.stringify(cards) })
    hasDraw && actionToDo.condition.push({ type: 'Draws', value: JSON.stringify(handDraws) })

    return actionToDo
  }
  if (betInBoard && !(hasPair || hasDraw)) {
    // Should fold
    return {
      action: ActionHint.FOLD,
      amount: 0,
      condition: [{ type: 'BetInBoard' }, { type: 'NoGoodHand' }]
    }
  }

  // 2. Check, Fold, or first on player.actions
  return getSoftAction({ player })
}

const Turn: BotActionCallback = function ({ game, table, player }): BotActionResponse {
  //
  const cards = getPlayingCards({ player, table })

  const { isAggressor: isPreFlopAggressor, aggressorEvent } = getAggressorInStreet('deal', { table, player })

  const handDraws = analyzeDraws({ table, player })
  const hasDraw = handDraws.draws.length > 0
  const hasPair = HandDetectionUtils.hasAPair()(cards)
  const hasTwoPair = HandDetectionUtils.hasTwoPair()(cards)
  const hasATopPair = HandDetectionUtils.hasATopPair()(cards)

  const betInBoard = !(table.biggestBet === 0)

  const hasExcellentHand = handDraws.isStrong || hasDraw || hasTwoPair || hasATopPair

  /**
   * Aggressor
   * 1.- put pressure Bet 1/2 pot
   * 2.- Call if a bet in board - if hasExcellentHand
   * 3.-
   */

  // Is Aggressor
  if (isPreFlopAggressor && !betInBoard) {
    const action = ActionHint.BET
    const amount = Math.round(table.mainPot * 0.5)
    const actionToDo = doBetActionOrAllIn({ action, amount, table, player })
    actionToDo.condition.push(...[{ type: 'IsPreFlopAggressor' }, { type: 'NoOtherBet' }])
    return actionToDo
  }

  // Aggressor should call a bet with ExcellentHand
  if (isPreFlopAggressor && hasExcellentHand) {
    if (betInBoard) {
      const action = ActionHint.CALL
      const amount = getAmountForAction({ action, player, table })
      const actionToDo = doBetActionOrAllIn({ action, amount, table, player })
      actionToDo.condition.push(...[{ type: 'IsPreFlopAggressor' }, { type: 'ExcellentHand' }])
      return actionToDo
    }

    return {
      action: ActionHint.CHECK,
      amount: 0,
      condition: [{ type: 'IsPreFlopAggressor' }, { type: 'ExcellentHand' }]
    }
  }

  // Non Aggressor should call the C-Bet with a pair or draw

  // Bet exist
  if (!isPreFlopAggressor && betInBoard && (hasPair || hasDraw)) {
    // Should call
    const action = ActionHint.CALL
    const amount = getAmountForAction({ action, player, table })
    const actionToDo = doBetActionOrAllIn({ action, amount, table, player })

    hasPair && actionToDo.condition.push({ type: 'Pair', value: JSON.stringify(cards) })
    hasDraw && actionToDo.condition.push({ type: 'Draws', value: JSON.stringify(handDraws) })

    return actionToDo
  }

  const isOtherPlayerBluffing = getChance(0.5)
  if (betInBoard && (isOtherPlayerBluffing || hasExcellentHand)) {
    const action = ActionHint.RAISE
    const amount = getAmountForAction({ action, player, table })
    const actionToDo = doBetActionOrAllIn({ action, amount, table, player })

    hasExcellentHand && actionToDo.condition.push({ type: 'ExcellentHand' })
    isOtherPlayerBluffing && actionToDo.condition.push({ type: 'IsBluffing' })

    return actionToDo
  }

  // 3. Check, Fold, or first on player.actions
  return getSoftAction({ player })
}

const River: BotActionCallback = function ({ player, table }) {
  const cards = getPlayingCards({ player, table })

  const handDraws = analyzeDraws({ table, player })
  const hasTwoPair = HandDetectionUtils.hasTwoPair()(cards)

  const betInBoard = !(table.biggestBet === 0)

  if (!betInBoard) {
    return {
      action: ActionHint.CHECK,
      amount: 0,
      condition: [{ type: 'NothingToDo' }]
    }
  }

  // Call bet two pair or better
  if (betInBoard && (hasTwoPair || handDraws.isStrong)) {
    const action = ActionHint.CALL
    const amount = getAmountForAction({ action, player, table })
    const actionToDo = doBetActionOrAllIn({ action, amount, table, player })

    hasTwoPair && actionToDo.condition.push({ type: 'TwoPair', value: JSON.stringify(cards) })
    handDraws && actionToDo.condition.push({ type: 'Draws', value: JSON.stringify(handDraws) })

    return actionToDo
  }

  return getSoftAction({ player })
}

const PreFlop: BotActionCallback = ({ table, player }) => {
  const cards = getPlayingCards({ player, table })
  if (cards.length === 0) {
    throw new Error('PreFlop: Cards cannot be empty')
  }

  const checkPremiumHand = () => {
    const conditions = [
      {
        type: 'PairOf',
        value: 'A',
        callback: HandDetectionUtils.hasPairOfCard
      },
      {
        type: 'PairOf',
        value: 'K',
        callback: HandDetectionUtils.hasPairOfCard
      },
      {
        type: 'PairOf',
        value: 'Q',
        callback: HandDetectionUtils.hasPairOfCard
      },
      {
        type: 'ValueOf',
        value: ['A', 'K'],
        callback: HandDetectionUtils.hasValueOfCards
      }
    ]
    const matchedConditions = conditions.filter(({ type, value, callback }) => {
      // @ts-expect-error
      return callback(value)(cards)
    })

    return matchedConditions.reduce(
      (acc, { type, value }) => {
        acc.push({ type, value })
        return acc
      },
      [] as { type: string; value: unknown }[]
    )
  }

  const checkGoodHand = () => {
    const conditions = [
      {
        type: 'Pair',
        // @ts-ignore
        value: undefined,
        callback: HandDetectionUtils.hasAPair
      },
      {
        type: 'TwoHigh',
        // @ts-ignore
        value: undefined,
        callback: HandDetectionUtils.hasTwoHightCards
      },
      {
        type: 'SuitedConnectors',
        // @ts-ignore
        value: undefined,
        callback: HandDetectionUtils.hasSuitedConnectors
      }
    ]
    const matchedConditions = conditions.filter(({ type, value, callback }) => {
      return callback()(cards)
    })

    return matchedConditions.reduce(
      (acc, { type, value }) => {
        acc.push(value ? { type, value } : { type })
        return acc
      },
      [] as { type: string; value?: unknown }[]
    )
  }

  const goodHandConditions = checkGoodHand()
  const premiumHandConditions = checkPremiumHand()

  const hasGoodHand = goodHandConditions.length > 0
  const hasPremiumHand = premiumHandConditions.length > 0
  logger.debug('LOG: > PreFlop > hasGoodHand:', hasGoodHand)
  logger.debug('LOG: > PreFlop > hasPremiumHand:', hasPremiumHand)

  // NOTE: Look at ./BotGuidelinePP.md

  const isBigBlindBotTurn = table.bigBlindPlayerId === player.id
  if (isBigBlindBotTurn && table.biggestBet === table.bigBlindAmount) {
    if (hasGoodHand || hasPremiumHand) {
      // Bet or AllIn
      const amount = getAmountForAction({ action: ActionHint.RAISE, table, player })
      const actionToDo = doBetActionOrAllIn({ amount, action: ActionHint.RAISE, table, player })
      hasGoodHand && actionToDo.condition.push(...goodHandConditions)
      hasPremiumHand && actionToDo.condition.push(...premiumHandConditions)
      actionToDo.condition.push({ type: 'bigBlindTurn' })
      return actionToDo
    }

    return {
      action: ActionHint.CHECK,
      amount: 0,
      condition: [{ type: 'bigBlindTurn' }]
    }
  }

  // No raise of other players
  const couldBet = table.biggestBet === table.bigBlindAmount
  logger.debug('LOG: > PreFlop > couldBet:', couldBet)

  // No bets ,try to bet if it has good cards
  if (couldBet && hasGoodHand) {
    // Bet or AllIn
    const action = ActionHint.RAISE
    const amount = getAmountForAction({ action, table, player })
    const actionToDo = doBetActionOrAllIn({ action, amount, table, player })
    actionToDo.condition.push(...goodHandConditions)
    return actionToDo
  }

  // Player have bet
  const anyPlayerHaveRaise = table.biggestBet > 0
  logger.debug('LOG: > PreFlop > anyPlayerHaveRaise:', anyPlayerHaveRaise)

  // There is a bet or raise. Possible actions: raise, call, allIn or fold
  // Check for premium cards
  const shouldReRaise = anyPlayerHaveRaise && hasPremiumHand
  logger.debug('LOG: > PreFlop > shouldReRaise:', shouldReRaise)
  // Raise
  if (shouldReRaise) {
    // try to handle re raise or all in
    const action = ActionHint.RAISE
    const amount = getAmountForAction({ action, table, player })
    const actionToDo = doBetActionOrAllIn({ action, amount, table, player })
    actionToDo.condition.push(...premiumHandConditions)
    return actionToDo
  }
  // Call
  const shouldCall = anyPlayerHaveRaise && hasGoodHand
  logger.debug('LOG: > PreFlop > shouldCall:', shouldCall)
  if (shouldCall) {
    const action = ActionHint.CALL
    const amount = getAmountForAction({ action, table, player })
    const actionToDo = doBetActionOrAllIn({ action, amount, table, player })
    actionToDo.condition.push(...goodHandConditions)
    return actionToDo
  }
  // No bets, no premium hand
  if (!(anyPlayerHaveRaise && hasPremiumHand)) {
    const conditions: BotActionCondition[] = []
    !hasPremiumHand && conditions.push({ type: 'NoGoodHand' })
    !anyPlayerHaveRaise && conditions.push({ type: 'NoBets' })

    return {
      action: ActionHint.FOLD,
      amount: 0,
      condition: conditions
    }
  }
  // Fold
  return getSoftAction({ player }, { condition: [{ type: 'NoGoodHand' }] })
}

export const StreetsActions = {
  PreFlop,
  Flop,
  Turn,
  River
}

export const doBetActionOrAllIn = ({
  action,
  amount,
  table,
  player
}: {
  action: ActionHint
  amount: number
  table: TableSnapshot
  player: Player
}): BotActionResponse => {
  if (![ActionHint.RAISE, ActionHint.BET, ActionHint.CALL].includes(action)) {
    throw new Error(`doBetActionOrAllIn: given action is not supported: ${action}`)
  }

  if (player.chips <= table.minimumRaise || player.chips <= table.biggestBet || player.chips <= amount) {
    // Player chips isn't enough
    return {
      action: ActionHint.ALLIN,
      amount: player.chips,
      condition: [{ type: 'InsufficientChips' }]
    }
  }

  const isBetAction = [ActionHint.RAISE, ActionHint.BET].includes(action)

  const amountOrMinRaise = amount <= table.minimumRaise ? table.minimumRaise : amount

  if (isBetAction && amountOrMinRaise <= table.biggestBet) {
    // Amount isn't enough
    // All In
    return {
      action: ActionHint.ALLIN,
      amount: player.chips,
      condition: [
        {
          type: 'TableMinimumRaise',
          value: table.minimumRaise
        }
      ]
    }
  }

  return {
    action,
    amount: isBetAction ? amountOrMinRaise : 0,
    condition: []
  }
}

const getAmountForAction = ({
  action,
  player,
  table
}: { action: ActionHint; player: Player; table: TableSnapshot }) => {
  const { mainPot, bigBlindAmount, biggestBet } = table
  const actionsMap: Record<string, number> = {
    [ActionHint.ALLIN]: 0, // noop. Handled by table doAllInForany
    [ActionHint.BET]: getBetAmount({ mainPot, playerChips: player.chips, bigBlindAmount }),
    [ActionHint.BLANK]: 0,
    [ActionHint.CALL]: 0, // noop. Handled by table onPokerTablePlayerCalled
    [ActionHint.CHECK]: 0,
    [ActionHint.FOLD]: 0,
    [ActionHint.RAISE]: getRaiseAmount({ biggestBet, playerChips: player.chips })
  }

  return actionsMap[action]
}

export const getBetAmount = ({
  playerChips,
  bigBlindAmount
}: {
  mainPot: number
  playerChips: number
  bigBlindAmount: number
}): number => {
  const betAmount = bigBlindAmount * 3
  return Math.min(betAmount, playerChips)
}

export const getRaiseAmount = ({ biggestBet, playerChips }: { biggestBet: number; playerChips: number }): number => {
  const bet = 3 * biggestBet
  const raiseAmount = Math.min(bet, playerChips)
  return Math.floor(raiseAmount)
}

export function getPlayingCards(args: { player: Player; table: TableSnapshot }) {
  return [...args.player.cards, ...args.table.communityCards] as CardString[]
}

type SoftActionOptions = {
  condition: BotActionCondition[]
}
export function getSoftAction(
  { player }: { player: Player },
  opts: SoftActionOptions = { condition: [{ type: 'NoMatch' }] }
) {
  const condition = opts?.condition ?? []

  // CHECK
  if (player.actions.includes(ActionHint.CHECK)) {
    return {
      action: ActionHint.CHECK,
      amount: 0,
      condition
    }
  }
  // FOLD
  if (player.actions.includes(ActionHint.FOLD)) {
    return {
      action: ActionHint.FOLD,
      amount: 0,
      condition
    }
  }
  condition.push({ type: 'DefaultPlayerAction' })
  // DEFAULT
  return {
    action: player.actions[0],
    amount: 0,
    condition
  }
}
